/**
 * Business rules for postponing (extending) a contract's expiration date.
 *
 * A postponement only makes sense while the contract is still a live pawn:
 * active (`null` / `''`), passive (`'!'`) or expired (`'!!'`). Once the
 * contract has been settled the operation is meaningless and must be blocked:
 *
 * - `OK`  - redeemed (fully paid) contract
 * - `INV` - goods moved into shop stock (inventoried)
 * - `EXP` - goods that left the stock (sold / removed)
 * - `ANL` - cancelled (annulled) contract
 *
 * The codes mirror `backend/src/domain/corrections.ts`.
 */

/** Contract statuses that can never be postponed. */
export const NON_POSTPONABLE_CONTRACT_STATUSES = ['OK', 'INV', 'EXP', 'ANL'] as const;

export type NonPostponableContractStatus = (typeof NON_POSTPONABLE_CONTRACT_STATUSES)[number];

/** Long-form status labels that map onto a blocking status code. */
const STATUS_ALIASES: Record<string, NonPostponableContractStatus> = {
    ANULAT: 'ANL',
    ANULATA: 'ANL',
};

/** Contract shape needed by the postponement rules (legacy and modern status field). */
export interface PostponableContract {
    Statut?: string | null;
    status?: string | null;
}

/** Uppercased, trimmed status code with long-form labels folded onto their code. */
export const normalizeContractStatus = (status: string | null | undefined): string => {
    const normalized = (status ?? '').trim().toUpperCase();
    return STATUS_ALIASES[normalized] ?? normalized;
};

/**
 * Whether a status code forbids postponement. Unknown / empty statuses are
 * treated as active, so only the explicitly settled states are blocked.
 */
export const isNonPostponableStatus = (status: string | null | undefined): boolean =>
    (NON_POSTPONABLE_CONTRACT_STATUSES as readonly string[]).includes(
        normalizeContractStatus(status),
    );

/**
 * Status code that blocks the postponement of the given contract, or `null`
 * when nothing blocks it. Used to explain the disabled Save button. The legacy
 * `Statut` field wins over the modern `status` alias.
 */
export const getPostponeBlockingStatus = (
    contract: PostponableContract | null | undefined,
): NonPostponableContractStatus | null => {
    if (!contract) {
        return null;
    }
    const normalized = normalizeContractStatus(contract.Statut ?? contract.status);
    return isNonPostponableStatus(normalized)
        ? (normalized as NonPostponableContractStatus)
        : null;
};

/**
 * Whether the given contract may be postponed. `null` (no contract selected)
 * is not postponable either, which is what the Save button reflects.
 */
export const isContractPostponable = (contract: PostponableContract | null | undefined): boolean =>
    !!contract && getPostponeBlockingStatus(contract) === null;
